import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import nameMappings from '../utils/part_name_mappings.json';
import Disclaimer from './Disclaimer';

function PartsDisplay({ parts, partId, modelYear, vinNumber, customer, year, onReset }) {
    const [displayParts, setDisplayParts] = useState([]);

    useEffect(() => {
        if (!parts || parts.length === 0) {
            setDisplayParts([]);
            return;
        }

        const mapped = parts.map(part => {
            const mappedDescription = nameMappings.part_mappings[part.description] || part.description;
            const mappedKitType = nameMappings.kit_type_mappings[part.kit_type] || part.kit_type;
            
            return {
                ...part,
                display_description: mappedDescription,
                display_kit_type: mappedKitType,
                original_description: part.description,
                original_kit_type: part.kit_type
            };
        });

        setDisplayParts(mapped);
    }, [parts]);

    const generateLink = (partNumber) => {
        return `https://trucksuspension-parts.com/search.php?search_query=${encodeURIComponent(partNumber)}`;
    };

    // Generate direct link to this search
    const generateDirectLink = () => {
        const baseUrl = window.location.origin;
        const params = new URLSearchParams({
            customer: customer.name,
            vin: vinNumber,
            ...(year && { year: year })
        });
        return `${baseUrl}/?${params.toString()}`;
    };

    // Enhanced PDF Generation Function
    const handleSavePDF = () => {
        const doc = new jsPDF();
        
        // Color palette matching website
        const colors = {
            primaryBlue: [0, 61, 122],
            accentOrange: [237, 135, 32],
            darkGray: [44, 62, 80],
            lightGray: [236, 240, 241],
            white: [255, 255, 255],
            success: [39, 174, 96]
        };
        
        // Kit type colors for variety
        const kitColors = {
            'Suspension Base Kit': [0, 61, 122],
            'Torque Rod Assembly': [231, 76, 60],
            'Axle Kit': [39, 174, 96],
            'Shock Kit': [241, 196, 15],
            'Hardware & Fasteners': [155, 89, 182]
        };
        
        let yPos = 20;
        
        // ===== HEADER SECTION WITH COLORED BOX =====
        doc.setFillColor(...colors.primaryBlue);
        doc.rect(0, 0, 210, 50, 'F'); // Full width blue header
        
        // Chalmers logo text (white)
        doc.setTextColor(...colors.white);
        doc.setFontSize(24);
        doc.setFont(undefined, 'bold');
        doc.text('CHALMERS SUSPENSIONS', 105, 20, { align: 'center' });
        
        doc.setFontSize(16);
        doc.setFont(undefined, 'normal');
        doc.text('VIN Parts Lookup Report', 105, 30, { align: 'center' });
        
        doc.setFontSize(10);
        doc.text(new Date().toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        }), 105, 40, { align: 'center' });
        
        yPos = 60;
        
        // ===== INFO BOX WITH ORANGE ACCENT =====
        const boxHeight = modelYear ? 58 : 50; // Adjust height based on whether model year exists
        doc.setFillColor(...colors.lightGray);
        doc.roundedRect(15, yPos, 180, boxHeight, 3, 3, 'F');
        
        // Orange accent bar on left
        doc.setFillColor(...colors.accentOrange);
        doc.rect(15, yPos, 4, boxHeight, 'F');
        
        // Info text with consistent spacing
        doc.setTextColor(...colors.darkGray);
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        
        let infoYPos = yPos + 12;
        const labelX = 25;
        const valueX = 70;
        
        // VIN
        doc.text('VIN:', labelX, infoYPos);
        doc.setFont(undefined, 'normal');
        doc.text(vinNumber, valueX, infoYPos);
        
        infoYPos += 10;
        
        // Configuration
        doc.setFont(undefined, 'bold');
        doc.text('Configuration:', labelX, infoYPos);
        doc.setFont(undefined, 'normal');
        doc.text(partId, valueX, infoYPos);
        
        infoYPos += 10;
        
        // Model Year (if exists)
        if (modelYear) {
            doc.setFont(undefined, 'bold');
            doc.text('Model Year:', labelX, infoYPos);
            doc.setFont(undefined, 'normal');
            doc.text(modelYear.toString(), valueX, infoYPos);
            infoYPos += 10;
        }
        
        // Total Parts
        doc.setFont(undefined, 'bold');
        doc.text('Total Parts:', labelX, infoYPos);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(...colors.success);
        doc.text(displayParts.length.toString(), valueX, infoYPos);
        
        infoYPos += 10;
        
        // Quick Access Link (same format as other fields)
        doc.setFont(undefined, 'bold');
        doc.setTextColor(...colors.darkGray);
        doc.text('Quick Access:', labelX, infoYPos);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(...colors.primaryBlue);
        const directLink = generateDirectLink();
        doc.textWithLink('Click here to view this search in web app', valueX, infoYPos, { url: directLink });
        
        yPos += boxHeight + 20;
        
        // ===== PARTS SECTIONS =====
        const grouped = displayParts.reduce((acc, part) => {
            const type = part.display_kit_type;
            if (!acc[type]) acc[type] = [];
            acc[type].push(part);
            return acc;
        }, {});
        
        Object.entries(grouped).forEach(([kitType, kitParts], index) => {
            // Only check if header needs new page (needs at least 30mm for header + first row)
            if (yPos > 250) {
                doc.addPage();
                yPos = 20;
            }
            
            // Kit section header with colored background (aligned with table width)
            const kitColor = kitColors[kitType] || colors.darkGray;
            const tableStartX = 15;
            const tableWidth = 180;
            
            doc.setFillColor(...kitColor);
            doc.roundedRect(tableStartX, yPos - 5, tableWidth, 12, 2, 2, 'F');
            
            doc.setTextColor(...colors.white);
            doc.setFontSize(13);
            doc.setFont(undefined, 'bold');
            doc.text(kitType, tableStartX + 5, yPos + 3);
            
            // Part count badge
            doc.setFillColor(...colors.white);
            doc.circle(tableStartX + tableWidth - 10, yPos + 1, 4, 'F');
            doc.setTextColor(...kitColor);
            doc.setFontSize(9);
            doc.text(kitParts.length.toString(), tableStartX + tableWidth - 10, yPos + 2, { align: 'center' });
            
            yPos += 15;
            
            // Table data
            const tableData = kitParts.map(part => [
                part.display_description,
                part.part_number,
                part.quantity.toString(),
                'View Part'
            ]);
            
            // Enhanced table - autoTable handles pagination automatically
            autoTable(doc, {
                startY: yPos,
                margin: { left: tableStartX, right: 15 },
                tableWidth: tableWidth,
                head: [['Description', 'Part Number', 'Qty', 'Link']],
                body: tableData,
                theme: 'striped',
                rowPageBreak: 'avoid', // CRITICAL: Prevents rows from being split across pages
                showHead: 'everyPage', // Show header on each page if table spans multiple pages
                headStyles: {
                    fillColor: kitColor,
                    textColor: colors.white,
                    fontStyle: 'bold',
                    fontSize: 10,
                    halign: 'left'
                },
                alternateRowStyles: {
                    fillColor: [250, 250, 250]
                },
                styles: {
                    fontSize: 9,
                    cellPadding: 6,
                    lineColor: [220, 220, 220],
                    lineWidth: 0.1
                },
                columnStyles: {
                    0: { cellWidth: 80, fontStyle: 'normal' },
                    1: { cellWidth: 45, halign: 'center', fontStyle: 'bold', textColor: colors.primaryBlue },
                    2: { cellWidth: 20, halign: 'center', fillColor: [240, 248, 255] },
                    3: { cellWidth: 35, halign: 'center', textColor: colors.primaryBlue, fontStyle: 'bold' }
                },
                didDrawCell: (data) => {
                    // Add clickable links in "Link" column
                    if (data.column.index === 3 && data.cell.section === 'body') {
                        const part = kitParts[data.row.index];
                        const link = generateLink(part.part_number);
                        doc.link(
                            data.cell.x,
                            data.cell.y,
                            data.cell.width,
                            data.cell.height,
                            { url: link }
                        );
                    }
                }
            });
            
            yPos = doc.lastAutoTable.finalY + 12;
        });
        
        // ===== FOOTER ON ALL PAGES =====
        const pageCount = doc.internal.getNumberOfPages();
        const pageHeight = doc.internal.pageSize.height;
        
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            
            // Footer background
            doc.setFillColor(...colors.lightGray);
            doc.rect(0, pageHeight - 20, 210, 20, 'F');
            
            // Footer text
            doc.setFontSize(8);
            doc.setTextColor(...colors.darkGray);
            doc.setFont(undefined, 'normal');
            doc.text(
                'Generated by Chalmers Suspensions VIN Lookup System',
                105,
                pageHeight - 12,
                { align: 'center' }
            );
            
            doc.setTextColor(...colors.primaryBlue);
            doc.setFont(undefined, 'bold');
            doc.text(
                `Page ${i} of ${pageCount}`,
                105,
                pageHeight - 6,
                { align: 'center' }
            );
        }
        
        // ===== SAVE PDF =====
        const filename = `Chalmers_VIN_${vinNumber}_Parts_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(filename);
    };

    const grouped = displayParts.reduce((acc, part) => {
        const type = part.display_kit_type;
        if (!acc[type]) acc[type] = [];
        acc[type].push(part);
        return acc;
    }, {});

    return (
        <div className="parts-display">
            <div className="action-buttons">
                <button onClick={onReset} className="back-btn">
                    ← Start New Search
                </button>
                <button onClick={handleSavePDF} className="pdf-btn">
                    Save as PDF →
                </button>
            </div>
            
            <div className="parts-header">
                <h2>Parts List</h2>
                {vinNumber && <p className="vin-number">VIN: {vinNumber}</p>}
                <p className="part-id">Configuration: {partId}</p>
                {modelYear && <p className="model-year">Model Year: {modelYear}</p>}
                <p className="parts-count">Total Parts: {displayParts.length}</p>
            </div>

            {Object.entries(grouped).map(([kitType, kitParts]) => (
                <div key={kitType} className="kit-section">
                    <h3>{kitType}</h3>
                    <div className="table-wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th>Description</th>
                                    <th>Part Number</th>
                                    <th>Qty</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {kitParts.map((part, idx) => (
                                    <tr key={idx}>
                                        <td>{part.display_description}</td>
                                        <td><code>{part.part_number}</code></td>
                                        <td>{part.quantity}</td>
                                        <td>
                                            <a 
                                                href={generateLink(part.part_number)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="view-btn"
                                            >
                                                View Part →
                                            </a>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}

            <Disclaimer />
        </div>
    );
}

export default PartsDisplay;